<template>
  <button class="grid-x align-middle settings-trigger" @click="openSettings">
    <AtomicUiIcon :icon="csgSettings" size="s" />

    <div :class="['medium padding-left-xxs padding-right-s', { seperator: brand === 'mcom' }]">Settings</div>
  </button>

  <AtomicUiSlideout id="wishlist-settings-slideout" :disable-teleport="true" title="Settings" position="right">
    <AtomicUiNotification v-if="errorMessage" severity="error" :closable="false" class="margin-bottom-m">
      {{ errorMessage }}
    </AtomicUiNotification>
    <AtomicUiInputTextBasic
      v-model="wishlistSettingsForm.listName"
      :class="['input-create-modal', { 'input-error': listNameErrorMessage }]"
      name="listName"
      :required="true"
      :label="inputLabel"
      rule="firstName"
      @blur="handleBlur"
      @keyup="validateListName"
      @focus="handleFocus"
    />

    <div :class="['form-error align-middle', { 'error-visible': listNameErrorMessage }]">
      <AtomicUiIcon :icon="csgErrorRed" size="m" />
      <span class="margin-left-xxs"> {{ listNameErrorMessage }} </span>
    </div>
    <div class="grid-x cell align-middle margin-top-xs">
      <AtomicUiCheckboxBasic
        v-model="wishlistSettingsForm.publicCheckbox"
        :required="false"
        name="isPublicCheckbox"
        :options="wishlistSettingsForm.publicOptions"
      />
      <a class="padding-left-xxs link-body learn-more" href="javascript:void(0)" @click="learnMoreClicked">
        {{ learnMoreToggle ? 'Learn More' : 'Hide' }}</a
      >
      <p v-if="!learnMoreToggle" class="margin-top-xs small">
        {{ CREATE_LIST_MODAL.LEARN_MORE.SHARE_LIST_MSG }}
      </p>
    </div>
    <div class="grid-x cell options-wrapper margin-top-xs">
      <AtomicUiCheckboxBasic
        v-model="wishlistSettingsForm.defaultCheckbox"
        :required="false"
        name="isDefaultCheckbox"
        :options="wishlistSettingsForm.defaultOptions"
      />
    </div>
    <div class="grid-y margin-top-m">
      <button class="button primary margin-bottom-xs" :disabled="isSaveDisabled" @click="saveSettings">
        <AtomicUiSpinner v-if="isloading" color="white" />
        <span v-else>Save</span>
      </button>
      <button class="button secondary margin-bottom-xs" @click="toggleSettingsSlideout">Cancel</button>
      <div class="grid-x align-center">
        <AtomicUiButton class="link-body" variant="link" @click="handleDeleteClick"> Delete this list </AtomicUiButton>
      </div>
    </div>
  </AtomicUiSlideout>
</template>

<script setup>
import { csgSettings, csgErrorRed } from '@csgSvgIcons/icons';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { CREATE_LIST_MODAL, headers, brand } from 'config';
import { wishListInputRules } from '#utils/CustomValidationRules';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { wishlistErrorAnalytics } from '~/utils/AnalyticsUtil.js';

const updateListStore = useUpdateListStore();
const getSelectedListStore = useGetSelectedListStore();
const slideoutStore = useSlideoutStore();

const updateListError = computed(() => updateListStore.updateListError);
const updateListErrorDetails = computed(() => updateListStore.updateListErrorDetails);
const { wishlistGuid, wishlistName, wishlistSearchable, isDefaultWishlist } = useWishlistDetails();

const inputLabel = ref('');

const wishlistSettingsForm = reactive({
  listName: '',
  publicCheckbox: [],
  defaultCheckbox: [],
  publicOptions: [{ id: 'public_checkbox', label: 'Make this list public', value: 'true' }],
  defaultOptions: [],
});

const isloading = ref(false);
const learnMoreToggle = ref(true);
const listNameErrorMessage = ref('');
const errorMessage = ref('');

watch(
  wishlistName,
  (newName) => {
    if (!wishlistSettingsForm.listName) {
      inputLabel.value = newName;
    }
  },
  { immediate: true },
);

const hasError = computed(() => {
  const isPublicChanged = wishlistSettingsForm.publicCheckbox.length > 0 !== wishlistSearchable.value;
  const isDefaultChanged = wishlistSettingsForm.defaultCheckbox.length > 0 !== isDefaultWishlist.value;
  const isNameChanged = wishlistSettingsForm.listName.trim() && wishlistSettingsForm.listName.trim() !== wishlistName.value;
  return isPublicChanged || isDefaultChanged || isNameChanged;
});

const isSaveDisabled = computed(() => isloading.value || !hasError.value || !!listNameErrorMessage.value);

const learnMoreClicked = () => {
  learnMoreToggle.value = !learnMoreToggle.value;
};

const handlePublicDefaultChecks = () => {
  if (wishlistSearchable.value) {
    wishlistSettingsForm.publicCheckbox.push('true');
  }
  if (wishlistSettingsForm.defaultOptions.length === 0) {
    const isDefault = isDefaultWishlist.value;
    wishlistSettingsForm.defaultOptions.push({
      id: 'default_checkbox',
      label: 'Set as default list',
      value: 'true',
      disabled: isDefault,
    });
    if (isDefault) {
      wishlistSettingsForm.defaultCheckbox.push('true');
    }
  }
  slideoutStore.toggle('wishlist-settings-slideout');
};

const toggleSettingsSlideout = () => {
  listNameErrorMessage.value = '';
  wishlistSettingsForm.listName = '';
  inputLabel.value = wishlistName.value;
  isloading.value = false;
  wishlistSettingsForm.publicCheckbox = [];
  wishlistSettingsForm.defaultCheckbox = [];
  wishlistSettingsForm.defaultOptions = [];
  errorMessage.value = '';
  handlePublicDefaultChecks();
};

const openSettings = () => {
  fireTag('link', {
    event_name: 'wishlist open settings',
    link_name: 'wishlist open settings',
    wishlist_id: wishlistGuid.value,
  });
  toggleSettingsSlideout();
};

const validateListName = () => {
  listNameErrorMessage.value = wishListInputRules(wishlistSettingsForm.listName);
};

const handleFocus = () => {
  inputLabel.value = 'List name';
};

const handleBlur = () => {
  validateListName();
  if (!wishlistSettingsForm.listName) {
    inputLabel.value = wishlistName.value;
  }
};

const handleDeleteClick = () => {
  toggleSettingsSlideout();
  slideoutStore.toggle('wishlist-delete-slideout');
};

const saveSettings = async () => {
  if (isSaveDisabled.value) {
    return;
  }

  const listPayload = {
    searchable: wishlistSettingsForm.publicCheckbox.includes('true'),
  };

  if (wishlistSettingsForm.listName.trim() && wishlistSettingsForm.listName.trim() !== wishlistName.value) {
    listPayload.name = wishlistSettingsForm.listName;
  }

  if (wishlistSettingsForm.defaultCheckbox.length > 0 && !isDefaultWishlist.value) {
    listPayload.defaultList = wishlistSettingsForm.defaultCheckbox.includes('true');
  }

  isloading.value = true;
  errorMessage.value = '';

  await updateListStore.updateList({
    payload: {
      CustomerList: {
        list: [listPayload],
      },
    },
    listGuid: wishlistGuid.value,
    headers,
  });

  isloading.value = false;

  if (updateListError.value) {
    wishlistErrorAnalytics(updateListErrorDetails.value, fireTag);
    errorMessage.value = updateListError.value;
    return;
  }

  const isPublic = wishlistSettingsForm.publicCheckbox.includes('true');
  const isDefault = wishlistSettingsForm.defaultCheckbox.includes('true');
  const linkName = `wishlist save: ${isPublic ? 'public' : 'private'}${isDefault ? '-default' : '-not default'}`;

  fireTag('link', {
    event_name: 'wishlist save settings',
    link_name: linkName,
    wishlist_id: wishlistGuid.value,
  });

  getSelectedListStore.updateWishlist({
    name: wishlistSettingsForm.listName.trim() ? wishlistSettingsForm.listName : wishlistName.value,
    searchable: !!wishlistSettingsForm.publicCheckbox.length,
    defaultList: !!wishlistSettingsForm.defaultCheckbox.length,
  });

  toggleSettingsSlideout();
};
</script>

<style lang="scss" scoped>
div.seperator {
  position: relative;
  cursor: pointer;
  &:before {
    content: '';
    height: rem-calc(13);
    width: rem-calc(1);
    position: absolute;
    left: rem-calc(73);
    top: rem-calc(4);
    background-color: $gray-4-color;
  }
}

.form-error {
  color: $error-color;
  display: none;
}

.options-wrapper {
  position: relative;
}

.error-visible {
  display: flex;
}

:deep(input.input-create-modal) {
  border-radius: rem-calc(5);
}

.learn-more {
  margin-top: rem-calc(2);
}
:deep(input.input-error) {
  border: 1px solid $error-color;
}

:deep(.input-error + label),
:deep(label[for='listName']) {
  color: $error-color;
}
</style>
